import { Edit, Trash, User, Users } from 'react-feather';
import { useHistory } from 'react-router-dom';
import { useModal } from 'react-modal-hook';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import { Permission } from '../../../__generated__/graphql';
import { assetUrl, encodeUrl } from '../../../common/helpers';
import { useNotifier } from '../../../common/Notifications/NotificationProvider';
import { useSession } from '../../Auth/SessionProvider';
import AddTeamMemberForm from './AddTeamMemberForm';
import CopyLink from '../../../common/Common/CopyLink';
import DeleteTeamMutation from './DeleteTeamMutation';
import EditTeamForm from './EditTeamForm';
import GetTeamQuery from './GetTeamQuery';
import OptionsDropdown, { Option } from '../../../common/Common/OptionsDropdown';
import TeamMemberCard from './TeamMemberCard';
import UI from '../../../common/UI';
import useConfirmModal from '../../../common/useConfirmModal';
import useDocumentTitle from '../../../common/useDocumentTitle';
import useMutation from '../../../api/useMutation';
import useProject from '../../useProject';

export interface TeamPageProps {
  teamId: string;
}

const TeamPage = ({ teamId }: TeamPageProps) => {
  const { t } = useTranslation();
  const { hasPermission } = useSession();
  const notifier = useNotifier();
  const history = useHistory();
  const project = useProject();

  const hasEditPermission = hasPermission(project.id, [Permission.edit_registrations]);

  const { data } = useQuery(GetTeamQuery, {
    variables: {
      team_id: teamId,
    },
  });

  const team = data?.team;

  useDocumentTitle(team?.title || t('team'));

  const [openAddTeamMemberModal, closeAddTeamMemberModal] = useModal(() => (
    <UI.Modal maxWidth={600} close={closeAddTeamMemberModal}>
      <AddTeamMemberForm
        team={team}
        refetchQueries={[GetTeamQuery]}
        onClose={() => closeAddTeamMemberModal()}
      />
    </UI.Modal>
  ), [team]);

  const [openEditTeamModal, closeEditTeamModal] = useModal(
    () => (
      <UI.Modal close={closeEditTeamModal} maxWidth={600}>
        <EditTeamForm
          team={team}
          onSuccess={() => closeEditTeamModal()}
        />
      </UI.Modal>
    ),
    [team],
  );

  const [deleteTeam] = useMutation(
    DeleteTeamMutation, {
      variables: {
        id: team?.id,
      },
      onCompleted: () => {
        notifier.success(t('notifications.team_deleted'));

        history.push(`/${project.id}/participants/teams`);
      },
      onError: () => {
        notifier.error(t('something_went_wrong'));
      },
    },
  );

  const [confirmDelete] = useConfirmModal({
    title: t('delete_name', { name: team?.title }),
    body: t('delete_team_description'),
    onConfirm: () => deleteTeam(),
  }, [team]);

  if (!team) {
    return <UI.PageLoader />;
  }

  return (
    <>
      <UI.Hero>
        <UI.Section>
          <UI.GridContainer sc={{ columns: ['1fr', '1fr 1fr'] }}>
            <UI.H1 sc={{ muted: !team.active }}>
              {team.title}
            </UI.H1>

            {hasEditPermission && (
              <UI.Div sc={{ textAlign: 'right' }}>
                <OptionsDropdown sc={{ ml: 1 }}>
                  <Option
                    icon={<Trash />}
                    label={t('delete')}
                    onClick={() => confirmDelete()}
                  />
                </OptionsDropdown>
              </UI.Div>
            )}
          </UI.GridContainer>
        </UI.Section>
      </UI.Hero>

      <UI.Section>
        <UI.GridContainer sc={{ columns: ['1fr', '1fr 3fr'] }}>
          <UI.Div>
            <UI.GridContainer>
              <UI.FlexContainer sc={{ alignItems: 'center' }}>
                <UI.SectionHeading sc={{ mr: 1 }}>
                  {t('about')}
                </UI.SectionHeading>

                {hasEditPermission && (
                  <UI.Button
                    onClick={openEditTeamModal}
                    sc={{ blank: true, size: 'sm', color: 'gray.600' }}
                    aria-label={t('edit_team')}
                  >
                    <UI.Icon style={{ paddingTop: 3 }}>
                      <Edit />
                    </UI.Icon>
                  </UI.Button>
                )}
              </UI.FlexContainer>

              <UI.GridContainer sc={{ gutter: 0.5 }}>
                <UI.Div>
                  <UI.Legend>
                    {t('event')}
                  </UI.Legend>
                  <UI.Div>
                    <UI.Link
                      to={`/${project.id}/events/${team.event.id}`}
                      sc={{ secondary: true }}
                    >
                      {team.event.title}
                    </UI.Link>
                  </UI.Div>
                </UI.Div>

                <UI.Div>
                  <UI.Legend>
                    {t('team_captain')}
                  </UI.Legend>
                  <UI.Div>
                    <UI.Link
                      to={`/${project.id}/participants/${team.captain.id}?${encodeUrl({
                        event: team.event.id,
                      })}`}
                      sc={{ secondary: true }}
                    >
                      <UI.Icon>
                        <User />
                      </UI.Icon>
                      {' '}
                      {team.captain.full_name}
                    </UI.Link>
                  </UI.Div>
                </UI.Div>

                {team.related_teams.length > 1 && (
                  <UI.Div>
                    <UI.Legend>
                      {t('other_teams')}
                    </UI.Legend>
                    <UI.Div>
                      {team.related_teams.map((relatedTeam) => (
                        <UI.Div key={relatedTeam.id} sc={{ strong: relatedTeam.id === team.id }}>
                          <UI.Link
                            to={`/${project.id}/participants/teams/${relatedTeam.id}`}
                            sc={{ secondary: true }}
                          >
                            <UI.Icon>
                              <Users />
                            </UI.Icon>
                            {' '}
                            {relatedTeam.title}
                          </UI.Link>
                          {' '}
                          <UI.Badge>{relatedTeam.participants_count}</UI.Badge>
                        </UI.Div>
                      ))}
                    </UI.Div>
                  </UI.Div>
                )}

                {team.event.private_teams && (
                  <UI.Div>
                    <UI.Legend>
                      {t('access_code')}
                    </UI.Legend>
                    <CopyLink value={team.access_code} sc={{ secondary: true }}>
                      {team.access_code}
                    </CopyLink>
                  </UI.Div>
                )}
              </UI.GridContainer>
            </UI.GridContainer>
          </UI.Div>
          <UI.Div>
            <UI.GridContainer>
              <UI.FlexContainer sc={{ justifyContent: 'space-between', alignItems: 'center' }}>
                <UI.SectionHeading>
                  {t('team_members')}
                  {' '}
                  <UI.Badge>{team.participants_count}</UI.Badge>
                </UI.SectionHeading>

                {hasEditPermission && (
                  <UI.Button
                    sc={{ brand: 'secondary', size: 'md' }}
                    onClick={() => openAddTeamMemberModal()}
                  >
                    {t('add_team_member')}
                  </UI.Button>
                )}
              </UI.FlexContainer>

              <UI.Div>
                {team.registrations.length === 0 ? (
                  <UI.Card sc={{ padding: 8, textAlign: 'center', background: 'gray.50' }}>
                    <UI.Div>
                      <img src={assetUrl('images/participant.svg')} alt="" height={120} />
                    </UI.Div>
                    {t('no_team_members')}
                  </UI.Card>
                ) : (
                  <UI.GridContainer sc={{ gutter: 0.5 }}>
                    {team.registrations.map((registration) => (
                      <TeamMemberCard
                        registration={registration}
                        team={team}
                        key={registration.id}
                      />
                    ))}
                  </UI.GridContainer>
                )}
              </UI.Div>
            </UI.GridContainer>
          </UI.Div>

        </UI.GridContainer>
      </UI.Section>
    </>
  );
};

export default TeamPage;
